// frontend/src/components/CampaignManager/tabs/WerbetraegerTab.tsx
import React, { useState, useMemo } from 'react';
import {
  Box, Button, Stack, Typography, TableContainer, Table, TableHead, TableRow,
  TableCell, TableBody, Paper, IconButton, Chip, useMediaQuery, useTheme, TablePagination
} from '@mui/material';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { WerbetraegerCard } from '../MobileCards';
import { Werbetraeger } from '../types';

interface Props {
  werbetraeger: Werbetraeger[];
  deleteWerbetraeger: (id: number) => Promise<void>;
  onOpenDialog: (werbetraeger: Werbetraeger | null) => void;
}

export default function WerbetraegerTab({ werbetraeger, deleteWerbetraeger, onOpenDialog }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const paginatedWerbetraeger = useMemo(() => {
    const start = page * rowsPerPage;
    return werbetraeger.slice(start, start + rowsPerPage);
  }, [werbetraeger, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Werbeträger wirklich löschen?')) {
      await deleteWerbetraeger(id);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Werbeträger</Typography>
        <Button
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          startIcon={<Plus size={20} />}
          onClick={() => onOpenDialog(null)}
          sx={{ backgroundColor: '#003366' }}
        >
          {isSmallMobile ? 'Neu' : 'Neuer Werbeträger'}
        </Button>
      </Stack>

      {isMobile ? (
        werbetraeger.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Keine Werbeträger vorhanden</Typography>
          </Paper>
        ) : (
          <>
            {paginatedWerbetraeger.map((wt) => (
              <WerbetraegerCard key={wt.id} werbetraeger={wt} onEdit={onOpenDialog} onDelete={handleDelete} />
            ))}
            {werbetraeger.length > rowsPerPage && (
              <TablePagination
                component="div"
                count={werbetraeger.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[50, 100, 250, 500]}
                labelRowsPerPage="Pro Seite:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
              />
            )}
          </>
        )
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Bezeichnung</TableCell>
                <TableCell>Kurzbezeichnung</TableCell>
                <TableCell>Bemerkungen</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedWerbetraeger.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Keine Werbeträger vorhanden
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedWerbetraeger.map((wt) => (
                  <TableRow key={wt.id} hover>
                    <TableCell><strong>{wt.bezeichnung}</strong></TableCell>
                    <TableCell><Chip label={wt.kurz} color="secondary" size="small" /></TableCell>
                    <TableCell>{wt.bemerkungen || '-'}</TableCell>
                    <TableCell>
                      <Chip label={wt.active ? 'Aktiv' : 'Inaktiv'} color={wt.active ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => onOpenDialog(wt)}>
                        <PencilSimple size={18} />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(wt.id)}>
                        <Trash size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={werbetraeger.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[50, 100, 250, 500]}
            labelRowsPerPage="Pro Seite:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
          />
        </TableContainer>
      )}
    </Box>
  );
}
